import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Books,
  CloudArrowUp,
  Trash,
  CheckCircle,
  WarningCircle,
  CircleNotch,
  FilePdf,
  ArrowClockwise,
} from '@phosphor-icons/react';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;
const CHUNK_SIZE = 3 * 1024 * 1024;

export const LibraryPage = () => {
  const [books, setBooks] = useState([]);
  const [uploads, setUploads] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const pollingRef = useRef(null);

  const fetchBooks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/library/books`);
      setBooks(res.data.books || []);
      return res.data.books || [];
    } catch (e) {
      return [];
    }
  }, []);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  useEffect(() => {
    const hasProcessing = books.some((b) => b.status === 'processing');
    if (hasProcessing && !pollingRef.current) {
      pollingRef.current = setInterval(fetchBooks, 4000);
    } else if (!hasProcessing && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [books, fetchBooks]);

  const uploadFile = async (file) => {
    const key = file.name;
    setUploads((u) => ({ ...u, [key]: { percent: 0, status: 'uploading' } }));
    try {
      const init = await axios.post(`${API}/library/upload/init`, { filename: file.name });
      const uploadId = init.data.upload_id;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      for (let i = 0; i < totalChunks; i++) {
        const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const form = new FormData();
        form.append('upload_id', uploadId);
        form.append('chunk_index', i);
        form.append('chunk', blob, `${file.name}.part${i}`);
        await axios.post(`${API}/library/upload/chunk`, form);
        setUploads((u) => ({
          ...u,
          [key]: { percent: Math.round(((i + 1) / totalChunks) * 100), status: 'uploading' },
        }));
      }
      await axios.post(`${API}/library/upload/complete`, {
        upload_id: uploadId,
        filename: file.name,
        total_chunks: totalChunks,
      });
      setUploads((u) => {
        const copy = { ...u };
        delete copy[key];
        return copy;
      });
      toast.success(`"${file.name}" transféré — indexation en cours`);
      fetchBooks();
    } catch (e) {
      setUploads((u) => ({ ...u, [key]: { percent: 0, status: 'error' } }));
      toast.error(`Échec de l'upload de "${file.name}": ${e.response?.data?.detail || e.message}`);
    }
  };

  const handleFiles = async (fileList) => {
    const pdfs = Array.from(fileList).filter((f) => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfs.length === 0) {
      toast.error('Seuls les fichiers PDF sont acceptés');
      return;
    }
    for (const f of pdfs) {
      await uploadFile(f);
    }
  };

  const reindexBook = async (book) => {
    try {
      await axios.post(`${API}/library/books/${book.id}/reindex`);
      toast.success(`Relance de l'indexation de "${book.title}"`);
      fetchBooks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la relance');
    }
  };

  const deleteBook = async (book) => {
    if (!window.confirm(`Supprimer "${book.title}" de la bibliothèque ?`)) return;
    try {
      await axios.delete(`${API}/library/books/${book.id}`);
      toast.success('Livre supprimé');
      fetchBooks();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} Mo` : `${Math.round(bytes / 1024)} Ko`;
  };

  const statusBadge = (book) => {
    if (book.status === 'indexed') {
      return (
        <span data-testid={`book-status-${book.id}`} className="inline-flex items-center gap-1 text-xs bg-emerald-900/30 text-emerald-300 px-2 py-1 rounded-full">
          <CheckCircle size={14} weight="fill" /> Indexé — {book.chunks_count} extraits
          {book.ocr_pages > 0 && ` (OCR: ${book.ocr_pages} p.)`}
        </span>
      );
    }
    if (book.status === 'error') {
      return (
        <span data-testid={`book-status-${book.id}`} className="inline-flex items-center gap-1 text-xs bg-red-900/30 text-red-300 px-2 py-1 rounded-full" title={book.error}>
          <WarningCircle size={14} weight="fill" /> Erreur
        </span>
      );
    }
    return (
      <span data-testid={`book-status-${book.id}`} className="inline-flex items-center gap-1 text-xs bg-blue-900/30 text-blue-300 px-2 py-1 rounded-full">
        <CircleNotch size={14} className="animate-spin" /> Indexation... {book.progress || 0}%
      </span>
    );
  };

  const indexedCount = books.filter((b) => b.status === 'indexed').length;

  return (
    <div data-testid="library-page" className="max-w-5xl mx-auto py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-2">
        <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center">
          <Books size={28} weight="duotone" className="text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-white">Ma Bibliothèque</h1>
          <p className="text-slate-400 text-sm">
            Livres PDF indexés en RAG — utilisés par la Fiche Diagnostic et la Recherche Ciblée
          </p>
        </div>
      </div>

      <p className="text-slate-500 text-xs mb-6">
        {indexedCount} livre{indexedCount > 1 ? 's' : ''} indexé{indexedCount > 1 ? 's' : ''} sur {books.length}
      </p>

      {/* Dropzone */}
      <div
        data-testid="library-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors mb-8
          ${dragOver ? 'border-indigo-400 bg-indigo-900/20' : 'border-slate-700 bg-slate-900/50 hover:border-indigo-500/50'}`}
      >
        <CloudArrowUp size={44} weight="duotone" className="text-indigo-400 mx-auto mb-3" />
        <p className="text-white font-medium mb-1">Glissez-déposez vos livres PDF ici</p>
        <p className="text-slate-500 text-sm">ou cliquez pour sélectionner — plusieurs fichiers acceptés, upload par fragments (gros fichiers OK), OCR automatique pour les scans</p>
        <input
          ref={inputRef}
          data-testid="library-file-input"
          type="file"
          accept=".pdf"
          multiple
          className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
        />
      </div>

      {/* Uploads en cours */}
      {Object.entries(uploads).length > 0 && (
        <div className="space-y-3 mb-8">
          {Object.entries(uploads).map(([name, u]) => (
            <div key={name} data-testid={`upload-progress-${name}`} className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-300 flex items-center gap-2">
                  <FilePdf size={18} className="text-rose-400" /> {name}
                </span>
                <span className="text-xs text-slate-400">
                  {u.status === 'error' ? 'Échec' : `${u.percent}%`}
                </span>
              </div>
              <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${u.status === 'error' ? 'bg-red-500' : 'bg-indigo-500'}`}
                  style={{ width: `${u.percent}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Liste des livres */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-800">
          <h2 className="text-lg font-semibold text-white">Livres intégrés</h2>
        </div>
        {books.length === 0 ? (
          <p data-testid="library-empty" className="text-slate-500 text-sm text-center py-10">
            Aucun livre pour le moment. Uploadez vos premiers PDFs ci-dessus.
          </p>
        ) : (
          <ul className="divide-y divide-slate-800">
            {books.map((book) => (
              <li key={book.id} data-testid={`book-row-${book.id}`} className="px-6 py-4 flex items-center gap-4">
                <FilePdf size={28} weight="duotone" className="text-rose-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{book.title}</p>
                  <p className="text-xs text-slate-500">
                    {book.pages_count > 0 && `${book.pages_count} pages · `}
                    {formatSize(book.size_bytes)}
                    {book.status === 'error' && book.error && (
                      <span className="text-red-400"> — {book.error}</span>
                    )}
                  </p>
                </div>
                {statusBadge(book)}
                {book.status === 'error' && (
                  <button
                    data-testid={`reindex-book-${book.id}`}
                    onClick={() => reindexBook(book)}
                    className="p-2 rounded-lg text-slate-500 hover:text-indigo-400 hover:bg-slate-800 transition-colors"
                    title="Relancer l'indexation"
                  >
                    <ArrowClockwise size={18} />
                  </button>
                )}
                <button
                  data-testid={`delete-book-${book.id}`}
                  onClick={() => deleteBook(book)}
                  className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-slate-800 transition-colors"
                  title="Supprimer"
                >
                  <Trash size={18} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
